import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Marketplaces } from 'common/constants/enums';
import { productsNumber } from 'common/constants/values';
import {
  amazonProductAsinValidationSchema,
  walmartProductIdValidationSchema,
} from 'common/validationSchemas';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import * as yup from 'yup';

import amazonLogoUrl from '../../../../../../../../assets/images/logos/amazon-icon.png';
import walmartLogoUrl from '../../../../../../../../assets/images/logos/walmart-icon.png';
import { styles } from './product-adder.styles';

interface IProductAdderForm {
  product_id: string | number;
}

interface IProductAdderProps {
  currentProductsCount: number;
  open: boolean;
  onClose: () => void;
  onAddProduct: (productId: string, marketplace: Marketplaces) => Promise<void>;
}

export const ProductAdder: FC<IProductAdderProps> = ({ currentProductsCount, onAddProduct, onClose, open }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [notFoundError, setNotFoundError] = useState(false);

  const [localMarketplace, setLocalMarketplace] = useState<Marketplaces>(Marketplaces.Amazon);

  const isAmazon = localMarketplace === Marketplaces.Amazon;
  const label = isAmazon ? 'ASIN' : 'Walmart ID';
  const placeholder = isAmazon ? 'e.g. B08ZVWDQQ1' : 'e.g. 1840281673';

  const currentSchema = isAmazon
    ? amazonProductAsinValidationSchema
    : walmartProductIdValidationSchema;

  const {
    clearErrors,
    formState: { errors, isValid },
    handleSubmit,
    register,
    reset,
    setError,
    trigger,
    watch,
  } = useForm<IProductAdderForm>({
    mode: 'onChange',
    defaultValues: { product_id: '' },
    resolver: yupResolver(currentSchema as yup.ObjectSchema<{ product_id: string }>),
  });

  const productIdValue = watch('product_id');

  useEffect(() => {
    if (productIdValue) {
      trigger('product_id');
    } else {
      clearErrors('product_id');
    }
  }, [localMarketplace, productIdValue, trigger, clearErrors]);

  const handleMarketplaceChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value;
    if (value === Marketplaces.Amazon || value === Marketplaces.Walmart) {
      setLocalMarketplace(value);
    }
  };

  const onSubmit: SubmitHandler<IProductAdderForm> = async (data) => {
    setIsLoading(true);
    setNotFoundError(false);

    try {
      const sanitizedId = String(data.product_id).trim();
      await onAddProduct(sanitizedId, localMarketplace);

      reset({ product_id: '' });
      clearErrors();
      onClose();
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message === 'Not found') {
          setNotFoundError(true);
        } else if (error.message === 'Product already exists') {
          setError('product_id', {
            type: 'manual',
            message: 'This product is already in the list.',
          });
        } else {
          setError('product_id', {
            type: 'manual',
            message: error.message || 'Failed to fetch product.',
          });
        }
      } else {
        setError('product_id', {
          type: 'manual',
          message: 'An unknown error occurred.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset({ product_id: '' });
    clearErrors();
    setNotFoundError(false);
  };

  if (currentProductsCount >= productsNumber) {
    return null;
  }

  const showValidationError = !!errors.product_id && !!productIdValue;
  const hasError = showValidationError || notFoundError;

  return (
    <Dialog fullWidth maxWidth="xs" onClose={handleClose} open={open}>
        <DialogTitle sx={styles.dialogTitle}>
          <Typography fontWeight={600} variant="subtitle1">
            Add New Product
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          <Stack spacing={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="marketplace-label">Marketplace</InputLabel>
              <Select
                label="Marketplace"
                labelId="marketplace-label"
                onChange={handleMarketplaceChange}
                renderValue={(selected) => (
                  <Box sx={styles.selectValue}>
                    <Box
                      component="img"
                      src={selected === Marketplaces.Amazon ? amazonLogoUrl : walmartLogoUrl}
                      sx={styles.selectValueIcon}
                    />
                    <Typography variant="body2">
                      {selected === Marketplaces.Amazon ? 'Amazon' : 'Walmart'}
                    </Typography>
                  </Box>
                )}
                value={localMarketplace}
              >
                <MenuItem value={Marketplaces.Amazon}>
                  <Box sx={styles.menuItemBox}>
                    <Box component="img" src={amazonLogoUrl} sx={styles.menuItemIcon} />
                    <Typography variant="body2">Amazon</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={Marketplaces.Walmart}>
                  <Box sx={styles.menuItemBox}>
                    <Box component="img" src={walmartLogoUrl} sx={styles.menuItemIcon} />
                    <Typography variant="body2">Walmart</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <Box>
              <TextField
                {...register('product_id')}
                autoComplete="off"
                disabled={isLoading}
                error={hasError}
                fullWidth
                label={label}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (isValid && !isLoading && productIdValue) {
                      handleSubmit(onSubmit)();
                    }
                  }
                }}
                placeholder={placeholder}
                size="small"
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon color="action" sx={styles.searchIcon} />,
                  },
                }}
              />
              {hasError && (
                <FormHelperText error sx={styles.errorText}>
                  {(showValidationError ? errors.product_id?.message : '') ||
                    (notFoundError ? 'Product not found in database.' : '')}
                </FormHelperText>
              )}
              {notFoundError && isAmazon && productIdValue && (
                <MuiLink
                  component={RouterLink}
                  sx={styles.importLink}
                  target="_blank"
                  to={`/product-import?asin=${productIdValue}`}
                >
                  Click here to import this product from Amazon →
                </MuiLink>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button color="inherit" disabled={isLoading} onClick={handleClose} variant="text">
            Cancel
          </Button>
          <Button
            disabled={!isValid || isLoading || !productIdValue}
            onClick={handleSubmit(onSubmit)}
            type="button"
            variant="contained"
          >
            {isLoading ? <CircularProgress color="inherit" size={20} /> : 'Fetch Product'}
          </Button>
        </DialogActions>
      </Dialog>
  );
};
